import Phaser from 'phaser';
import { hubUnlocked, HUB_LOCK_HINT } from '../../config/hub';
import { LEVELS, LAST_LEVEL_ID } from '../../config/levels';
import { HUB_LEVELS } from '../../config/hubLevels';
import { ORIGIN_LEVELS } from '../../config/originEvent';
import { ProgressService } from '../../services/progressService';
import { LocalScoreService } from '../../services/scoreService';
import { saveStorage } from '../saveStorage';
import { sweepAndToastAchievements } from '../../ui/achievementToast';
import { getAudioService } from '../audio/AudioService';
import { getMusicService } from '../audio/MusicService';
import { INTRO_KEYS_REGISTRY } from './BootScene';
import { addMuteButton, addVolumeSlider } from '../audio/audioControls';
import { addFullscreenButton } from '../screenControls';
import { installDebugReturn } from '../debugMode';
import { hideLegalFooter, showLegalFooter } from '../../ui/legalFooter';
import { CODES_PURPLE, CODES_PURPLE_HOVER, openCodesPanel } from '../codesPanel';
import { FEEDBACK_GREEN, FEEDBACK_GREEN_HOVER, openFeedbackPanel } from '../feedbackPanel';
import { accountClient, onIdentityChange, signedInRole } from '../../account/accountUi';
import { inboxUnread } from '../../account/adminInbox';
import { drawStarfield } from '../starfield';
import { hideBootSplash } from '../bootSplash';

// Splash art ships as atlas art (assets/atlas/splash-{title,hambi,nugget}.png),
// which BootScene loads. There was once a glob over assets/reference/*.png
// here as well; the atlas copies always won, so all it ever loaded were the
// hand-art import masters (tower-*.source.png) — ~26 MB nothing displayed.
const splashKey = (name: string) => `splash-${name}`;

/**
 * MenuScene
 * "HAMBI WARZ" title splash: starfield backdrop, the logo, a hero-vs-horde
 * face-off (Hambi vs the Nugget Horde), and a glowing Play button.
 * Navigates to LevelSelectScene on Play. Best total score shown once fetched.
 */
export class MenuScene extends Phaser.Scene {
  /** Transient unlock hint under a locked THE HUB button. */
  private hubHint?: Phaser.GameObjects.Text;
  constructor() {
    super({ key: 'MenuScene' });
  }

  create(): void {
    hideBootSplash();
    // State achievements a save already satisfies (spec 2026-10-29: the
    // Diamond batch is earned on the next open by anyone who already did it).
    sweepAndToastAchievements(saveStorage());
    // The only route to the legal documents for a player with no account —
    // and for any build without VITE_SUPABASE_*, where the account button
    // never renders at all.
    showLegalFooter();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => hideLegalFooter());
    const W = 1280;
    const H = 720;
    const cx = W / 2;

    drawStarfield(this, W, H);

    // ── Title logo ("HAMBI WARZ") — its dark starfield blends into the backdrop ──
    if (this.textures.exists(splashKey('title'))) {
      // Soft gold glow behind the logo.
      this.add.ellipse(cx, 130, 760, 150, 0xf5c518, 0.12).setBlendMode(Phaser.BlendModes.ADD);
      const logo = this.add.image(cx, 130, splashKey('title')).setOrigin(0.5);
      logo.setScale(Math.min(1.15, 880 / logo.width));
      this.tweens.add({
        targets: logo,
        scale: logo.scale * 1.03,
        duration: 2200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.inOut',
      });
    } else {
      this.add
        .text(cx, 130, 'HAMBI TOWER DEFENSE', {
          fontSize: '52px',
          color: '#f5c518',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
    }

    // (Subtitle removed — the title logo already reads "HAMBI TOWER DEFENSE".)

    // ── Hero vs Horde face-off — flanking the centre so the Play column stays clear ──
    this.makeCard(250, 410, splashKey('hambi'), 0xf5c518, 'HAMBI', 'HERO');
    this.makeCard(1030, 410, splashKey('nugget'), 0xc0392b, 'NUGGET HORDE', 'INVADER');

    // ── Centre column: Shop button above Play, best score below ──
    this.makeShopButton(cx, 365);
    this.makePlayButton(cx, 440);
    this.makeHubButton(cx, 515);
    // Bottom-left corner, clear of the centred legal footer (spec 2026-10-09).
    this.makeCodesButton(110, 672);
    // FEEDBACK sits right above CODES (spec 2026-10-24).
    this.makeFeedbackButton(110, 612);
    // The admin's INBOX, top-left — built once the profile says "admin".
    this.makeInboxButton(84, 40);

    const scoreLabel = this.add
      .text(cx, 585, 'BEST TOTAL: —', { fontSize: '22px', color: '#9fb38c' })
      .setOrigin(0.5);

    this.add
      .text(cx, 668, 'Defend the forest picnic — repel the Nugget Horde.', {
        fontSize: '18px',
        color: '#7f8c8d',
        fontStyle: 'italic',
      })
      .setOrigin(0.5);

    // Async: sum best scores across the campaign, the Hub AND the Origin
    // Event — the same total the SCORE leaderboard ranks (config/leaderboard
    // totalScore).
    const scoreService = new LocalScoreService(saveStorage());
    void (async () => {
      try {
        const bests = await Promise.all(
          [...LEVELS, ...HUB_LEVELS, ...ORIGIN_LEVELS].map((l) => scoreService.best(l.id)),
        );
        const total = bests.reduce((sum, s) => sum + s, 0);
        scoreLabel.setText(total > 0 ? `BEST TOTAL: ${total}` : 'NO SCORES YET');
      } catch {
        // Scores are non-critical; leave placeholder text.
      }
    })();

    // Loop the episode-selection theme on the start screen too, mirroring
    // LevelSelectScene. Browser autoplay stays locked until the first gesture;
    // MusicService starts the track on unlock. Stop on shutdown so the next
    // scene's own track takes over cleanly.
    const music = getMusicService(this);
    music.playTrack('episode-selection');
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => music.stop());

    // ── Audio controls (the intro sting fires from the Play button) ──
    const audio = getAudioService(this);
    addMuteButton(this, 1235, 40, audio); // top-right corner; clear of Play (640,440)
    addFullscreenButton(this, 1195, 40); // left of the mute toggle
    addVolumeSlider(this, 1140, 690, audio); // bottom-right; clear of Play column

    installDebugReturn(this); // Esc → DemoScene when launched via ?debug
  }

  /**
   * Play a random intro sting. Fired from the Play button only — that click is
   * also the gesture that unlocks browser audio, so it reliably starts.
   * AudioService skips it while one is already playing, so it never overlaps.
   */
  private playIntroSting(): void {
    const introKeys = (this.registry.get(INTRO_KEYS_REGISTRY) as string[] | undefined) ?? [];
    if (introKeys.length === 0) return; // no intros bundled — stay silent
    const introKey = introKeys[Math.floor(Math.random() * introKeys.length)];
    getAudioService(this).playIntro(introKey);
  }

  /** A rounded "character card": image cover-scaled + clipped, framed, with labels. */
  private makeCard(
    x: number,
    y: number,
    key: string,
    frameColor: number,
    name: string,
    role: string,
  ): void {
    const w = 230;
    const h = 320;
    const radius = 18;
    const left = x - w / 2;
    const top = y - h / 2;

    // Backing panel (shows through if art is missing).
    const panel = this.add.graphics();
    panel.fillStyle(0x000000, 0.35);
    panel.fillRoundedRect(left, top, w, h, radius);

    if (this.textures.exists(key)) {
      const img = this.add.image(x, y, key);
      const s = Math.max(w / img.width, h / img.height); // cover
      img.setScale(s);
      const maskG = this.make.graphics();
      maskG.fillStyle(0xffffff);
      maskG.fillRoundedRect(left, top, w, h, radius);
      img.setMask(maskG.createGeometryMask());
    }

    // Frame.
    const border = this.add.graphics();
    border.lineStyle(4, frameColor, 1);
    border.strokeRoundedRect(left, top, w, h, radius);

    // Labels.
    this.add
      .text(x, top + h + 18, name, {
        fontSize: '22px',
        color: Phaser.Display.Color.IntegerToColor(frameColor).rgba,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.add.text(x, top + h + 44, role, { fontSize: '15px', color: '#9aa6ad' }).setOrigin(0.5);
  }

  /** Glowing, pulsing Play button. Kept at (cx, 440) for the e2e click target. */
  private makePlayButton(x: number, y: number): void {
    const w = 230;
    const h = 64;
    const left = x - w / 2;
    const top = y - h / 2;

    const bg = this.add.graphics();
    bg.fillStyle(0xf5c518, 1);
    bg.fillRoundedRect(left, top, w, h, 14);
    bg.lineStyle(3, 0xfff3b0, 1);
    bg.strokeRoundedRect(left, top, w, h, 14);

    const label = this.add
      .text(x, y, '▶  PLAY', { fontSize: '32px', color: '#1a140a', fontStyle: 'bold' })
      .setOrigin(0.5);

    // Invisible, generously-sized hit zone centred on (x, y) so the e2e click at
    // (640, 440) always lands.
    const hit = this.add.zone(x, y, w, h).setOrigin(0.5).setInteractive({ useHandCursor: true });

    const setHover = (on: boolean) => {
      bg.clear();
      bg.fillStyle(on ? 0xffd83a : 0xf5c518, 1);
      bg.fillRoundedRect(left, top, w, h, 14);
      bg.lineStyle(3, 0xfff3b0, 1);
      bg.strokeRoundedRect(left, top, w, h, 14);
    };
    hit.on('pointerover', () => setHover(true));
    hit.on('pointerout', () => setHover(false));
    hit.on('pointerup', () => {
      this.playIntroSting(); // intro sting fires on Play, then we head to level select
      this.scene.start('LevelSelectScene');
    });

    this.tweens.add({
      targets: label,
      scale: 1.06,
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    });
  }

  /**
   * THE HUB — the red button under Play (spec 2026-09-02-the-hub-design.md).
   * Locked until The Ember and Flame is won: greyed, and a tap flashes the
   * unlock hint instead of navigating. The gate is derived from progress (or
   * the `hub` dev flag), so a save that already beat 25 sees it live at once.
   * Kept at (cx, 515) for the e2e click target.
   */
  private makeHubButton(x: number, y: number): void {
    const w = 230;
    const h = 56;
    const left = x - w / 2;
    const top = y - h / 2;
    let unlocked = false;
    try {
      unlocked = hubUnlocked(new ProgressService(saveStorage(), LAST_LEVEL_ID));
    } catch (err) {
      console.warn('[MenuScene] hub gate read failed', err);
    }

    const bg = this.add.graphics();
    const draw = (hover: boolean) => {
      bg.clear();
      bg.fillStyle(unlocked ? (hover ? 0xd9483a : 0xc0392b) : 0x4a3a3a, 1);
      bg.fillRoundedRect(left, top, w, h, 12);
      bg.lineStyle(2, unlocked ? 0xffb3a7 : 0x6a5a5a, hover ? 1 : 0.85);
      bg.strokeRoundedRect(left, top, w, h, 12);
    };
    draw(false);
    this.add
      .text(x, y, unlocked ? 'THE HUB' : 'THE HUB · LOCKED', {
        fontSize: unlocked ? '26px' : '18px',
        color: unlocked ? '#ffffff' : '#a89a9a',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setName('menu-hub-label');

    const hit = this.add
      .zone(x, y, w, h)
      .setOrigin(0.5)
      .setName('menu-hub')
      .setInteractive({ useHandCursor: true });
    hit.on('pointerover', () => draw(true));
    hit.on('pointerout', () => draw(false));
    hit.on('pointerup', () => {
      if (unlocked) {
        this.scene.start('HubScene');
        return;
      }
      this.hubHint?.destroy();
      this.hubHint = this.add
        .text(x, y + 42, HUB_LOCK_HINT, { fontSize: '14px', color: '#e8a0a0', fontStyle: 'italic' })
        .setOrigin(0.5)
        .setName('menu-hub-hint');
      this.time.delayedCall(1800, () => {
        this.hubHint?.destroy();
        this.hubHint = undefined;
      });
    });
  }

  /**
   * Purple CODES button (white gift box + white label) in the bottom-left
   * corner: opens the redeem-codes panel over the menu (spec 2026-10-09).
   */
  private makeCodesButton(x: number, y: number): void {
    const w = 172;
    const h = 52;
    const left = x - w / 2;
    const top = y - h / 2;

    const bg = this.add.graphics();
    const draw = (hover: boolean) => {
      bg.clear();
      bg.fillStyle(hover ? CODES_PURPLE_HOVER : CODES_PURPLE, 1);
      bg.fillRoundedRect(left, top, w, h, 12);
      bg.lineStyle(2, 0xffffff, hover ? 1 : 0.85);
      bg.strokeRoundedRect(left, top, w, h, 12);
    };
    draw(false);

    this.add.image(x - 48, y, 'icon-gift').setDisplaySize(30, 30);
    this.add
      .text(x + 14, y, 'CODES', { fontSize: '24px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5);

    const hit = this.add
      .zone(x, y, w, h)
      .setOrigin(0.5)
      .setName('menu-codes')
      .setInteractive({ useHandCursor: true });
    hit.on('pointerover', () => draw(true));
    hit.on('pointerout', () => draw(false));
    hit.on('pointerup', () => openCodesPanel(this));
  }

  /**
   * Green FEEDBACK button (white label) right above CODES: opens the feedback
   * panel over the menu (spec 2026-10-24).
   */
  private makeFeedbackButton(x: number, y: number): void {
    const w = 172;
    const h = 52;
    const left = x - w / 2;
    const top = y - h / 2;
    const bg = this.add.graphics();
    const draw = (hover: boolean) => {
      bg.clear();
      bg.fillStyle(hover ? FEEDBACK_GREEN_HOVER : FEEDBACK_GREEN, 1);
      bg.fillRoundedRect(left, top, w, h, 12);
      bg.lineStyle(2, 0xffffff, hover ? 1 : 0.85);
      bg.strokeRoundedRect(left, top, w, h, 12);
    };
    draw(false);
    this.add
      .text(x, y, 'FEEDBACK', { fontSize: '24px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5);
    const hit = this.add
      .zone(x, y, w, h)
      .setOrigin(0.5)
      .setName('menu-feedback')
      .setInteractive({ useHandCursor: true });
    hit.on('pointerover', () => draw(true));
    hit.on('pointerout', () => draw(false));
    hit.on('pointerup', () => openFeedbackPanel(this));
  }

  /**
   * The admin-only INBOX button in the top-left corner (spec 2026-10-24),
   * with a red "!" to its right while a dev command has arrived since the
   * inbox was last opened. Nothing is drawn for anyone else. The profile
   * loads after the first frame, so this listens for it, and while the menu
   * is open it re-checks the "!" every 30 seconds.
   */
  private makeInboxButton(x: number, y: number): void {
    const w = 128;
    const h = 44;
    const parts: Phaser.GameObjects.GameObject[] = [];
    let bang: Phaser.GameObjects.Text | undefined;
    let built = false;
    // Not `scene.isActive()`: that is false all through create(), which is
    // exactly when the first check runs.
    let gone = false;

    const refreshBang = async () => {
      const client = accountClient();
      if (!client || !bang) return;
      const n = await inboxUnread(client);
      if (bang.active) bang.setVisible(n > 0);
    };
    const build = () => {
      if (built) return;
      built = true;
      const bg = this.add.graphics();
      const draw = (hover: boolean) => {
        bg.clear();
        bg.fillStyle(hover ? 0x3a4458 : 0x262d3c, 1);
        bg.fillRoundedRect(x - w / 2, y - h / 2, w, h, 10);
        bg.lineStyle(2, 0xffd23f, hover ? 1 : 0.8);
        bg.strokeRoundedRect(x - w / 2, y - h / 2, w, h, 10);
      };
      draw(false);
      const label = this.add
        .text(x, y, '✉ INBOX', { fontSize: '20px', color: '#ffffff', fontStyle: 'bold' })
        .setOrigin(0.5);
      bang = this.add
        .text(x + w / 2 + 16, y, '!', {
          fontSize: '30px',
          color: '#ff3b3b',
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 4,
        })
        .setOrigin(0.5)
        .setVisible(false)
        .setName('menu-inbox-alert');
      const hit = this.add
        .zone(x, y, w, h)
        .setName('menu-inbox')
        .setInteractive({ useHandCursor: true });
      hit.on('pointerover', () => draw(true));
      hit.on('pointerout', () => draw(false));
      hit.on('pointerup', () => this.scene.start('AdminInboxScene'));
      parts.push(bg, label, bang, hit);
      void refreshBang();
    };
    const sync = () => {
      if (gone) return;
      if (signedInRole() === 'admin') build();
      else if (built) {
        parts.splice(0).forEach((o) => o.destroy());
        bang = undefined;
        built = false;
      }
    };
    sync();
    const off = onIdentityChange(sync);
    const timer = this.time.addEvent({
      delay: 30_000,
      loop: true,
      callback: () => void refreshBang(),
    });
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      gone = true;
      off();
      timer.remove();
    });
  }

  /**
   * Secondary SHOP button (white diamond icon + label) above the Play button —
   * blue panel with a white outline so PLAY's gold stays the primary action.
   * Kept at (cx, 365) for the e2e click target.
   */
  private makeShopButton(x: number, y: number): void {
    const w = 190;
    const h = 52;
    const left = x - w / 2;
    const top = y - h / 2;

    const bg = this.add.graphics();
    const draw = (hover: boolean) => {
      bg.clear();
      bg.fillStyle(hover ? 0x2f7bd0 : 0x1e5fa8, 1);
      bg.fillRoundedRect(left, top, w, h, 12);
      bg.lineStyle(2, 0xffffff, hover ? 1 : 0.85);
      bg.strokeRoundedRect(left, top, w, h, 12);
    };
    draw(false);

    this.add
      .image(x - 46, y, 'diamond')
      .setDisplaySize(28, 28)
      .setTintFill(0xffffff);
    this.add
      .text(x + 14, y, 'SHOP', { fontSize: '24px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5);

    const hit = this.add.zone(x, y, w, h).setOrigin(0.5).setInteractive({ useHandCursor: true });
    hit.on('pointerover', () => draw(true));
    hit.on('pointerout', () => draw(false));
    hit.on('pointerup', () => this.scene.start('ShopScene'));
  }
}
